import { clsx } from 'clsx';
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { Flex } from '@repo/ui/Flex';
import { Toast, type ToastPosition } from '@repo/ui/Toast';
import { type ToastItem } from '@repo/ui/Toast/ToastRoot';

import s from './ToastGroup.module.css';

const PORTAL_ID = 'toast-portal';

interface ToastContainerProps {
  toasts: ToastItem[];
  hideToast: (id: string) => void;
}

export const ToastGroup = ({ toasts, hideToast }: ToastContainerProps) => {
  const [portalElement] = useState(() => {
    if (typeof document === 'undefined') return null;

    const existingElement = document.getElementById(PORTAL_ID);
    if (existingElement) return existingElement;

    const element = document.createElement('div');
    element.id = PORTAL_ID;
    document.body.appendChild(element);
    return element;
  });

  // Group toasts by position
  const toastsByPosition = useMemo(() => {
    const grouped: Record<ToastPosition, ToastItem[]> = {
      topLeft: [],
      topCenter: [],
      topRight: [],
      bottomRight: [],
      bottomCenter: [],
      bottomLeft: [],
    };

    toasts.forEach((toast) => {
      const position = toast.position ?? 'bottomRight';
      grouped[position].push(toast);
    });

    return grouped;
  }, [toasts]);

  const toastElements = useMemo(
    () => (
      <>
        {(
          Object.entries(toastsByPosition) as [ToastPosition, ToastItem[]][]
        ).map(([position, positionToasts]) => {
          if (positionToasts.length === 0) return null;

          return (
            <Flex
              key={position}
              className={clsx(s.group, s[`${position}Position`])}
              display="inline-flex"
              direction="column"
              gap="g-200"
            >
              {positionToasts.map((toast) => (
                <Toast
                  key={toast.id}
                  variant={toast.variant}
                  duration={toast.duration}
                  autoClose={toast.autoClose}
                  onClose={() => hideToast(toast.id)}
                >
                  {toast.children}
                </Toast>
              ))}
            </Flex>
          );
        })}
      </>
    ),
    [toastsByPosition, hideToast],
  );

  if (!portalElement) return null;
  return createPortal(toastElements, portalElement);
};
